import React from 'react';

/**
 * Shared Abyss DataTable overrides that make a table fit its wrapper width
 * without ever showing a horizontal scrollbar: columns are fixed-layout at
 * 100% width, cell text wraps instead of overflowing, and the DataTable's
 * own scroll container has horizontal overflow disabled.
 *
 * Spread this into a route's `DataTableWrapper` styled component alongside
 * any page-specific styling (borders, padding, header layout, etc.).
 */
export const NO_HORIZONTAL_SCROLL_TABLE_STYLES = {
  '& .abyss-data-table-root': {
    width: '100%',
    minWidth: 0,
  },
  '& table': {
    width: '100%',
    tableLayout: 'fixed',
  },
  '& th, & td': {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    verticalAlign: 'top',
  },
  '& .abyss-data-table-scroll': {
    overflowX: 'hidden',
    overflowY: 'hidden',
  },
} as const;

/**
 * Tracks the rendered width of a table wrapper element so column sizes can be
 * recalculated responsively (including during side-nav expand/collapse
 * transitions). Skips observing while there is no data to size columns for.
 */
export function useTableContainerWidth(
  containerRef: React.RefObject<HTMLElement | null>,
  rowCount: number,
  defaultWidth: number,
): number {
  const [width, setWidth] = React.useState(defaultWidth);

  React.useEffect(() => {
    const container = containerRef.current;

    if (!container || rowCount === 0) {
      return;
    }

    const updateWidth = () => {
      setWidth(container.getBoundingClientRect().width);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [rowCount]);

  return width;
}

export interface ResponsiveColumnConfig {
  min: number;
  max: number;
  flex: number;
}

/**
 * Distributes an available container width across a fixed set of columns.
 * Columns start at their `min` width and grow toward `max` proportional to
 * `flex`, in that priority order. Any width left over after every column
 * has reached its `max` is added to `remainderColumnKey`.
 */
export function getResponsiveColumnSizes<T extends Record<string, ResponsiveColumnConfig>>(
  containerWidth: number,
  columnLayout: T,
  remainderColumnKey: string,
): Record<keyof T, number> {
  const availableWidth = Math.max(containerWidth, 0);
  const entries = Object.entries(columnLayout) as [string, ResponsiveColumnConfig][];
  const minimumWidth = entries.reduce((sum, [, config]) => {return sum + config.min}, 0);

  if (availableWidth <= minimumWidth) {
    return Object.fromEntries(entries.map(([key, config]) => {return [key, config.min]})) as Record<keyof T, number>;
  }

  let remainingWidth = availableWidth - minimumWidth;
  const sizes: Record<string, number> = Object.fromEntries(
    entries.map(([key, config]) => {return [key, config.min]}),
  );
  let expandable = entries.filter(([, config]) => {return config.max > config.min});

  while (remainingWidth > 0 && expandable.length > 0) {
    const totalFlex = expandable.reduce((sum, [, config]) => {return sum + config.flex}, 0);
    let distributed = 0;

    expandable.forEach(([key, config]) => {
      const current = sizes[key] as number;
      const room = config.max - current;

      if (room <= 0) {
        return;
      }

      const share = remainingWidth * (config.flex / totalFlex);
      const increment = Math.min(room, Math.max(1, Math.floor(share)));

      sizes[key] = current + increment;
      distributed += increment;
    });

    if (distributed === 0) {
      break;
    }

    remainingWidth -= distributed;
    expandable = expandable.filter(([key, config]) => {return (sizes[key] as number) < config.max});
  }

  if (remainingWidth > 0) {
    sizes[remainderColumnKey] = (sizes[remainderColumnKey] as number) + remainingWidth;
  }

  return sizes as Record<keyof T, number>;
}
